import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../../../i18n";

const paths = new Set(["/messages", "/returns"]);
type Visits = Record<string, string>;
const VisitsContext = createContext<Visits>({});

/** Only these two internal workspaces may be return destinations. Never nest origins. */
export function supportDestination(raw: string | null): string | null {
  if (
    !raw ||
    raw.length > 4096 ||
    !raw.startsWith("/") ||
    raw.startsWith("//") ||
    raw.includes("\\")
  )
    return null;
  const url = new URL(raw, "https://customy.invalid");
  if (url.origin !== "https://customy.invalid" || !paths.has(url.pathname)) return null;
  const allowed =
    url.pathname === "/messages"
      ? new Set(["thread", "status", "channel", "q"])
      : new Set(["return", "status", "platform", "deadline", "q", "sort", "dir"]);
  for (const key of [...url.searchParams.keys()])
    if (!allowed.has(key)) url.searchParams.delete(key);
  return url.pathname + url.search;
}

function readVisits(key: string): Visits {
  try {
    const stored = JSON.parse(sessionStorage.getItem(key) || "{}");
    return Object.fromEntries(
      [...paths].flatMap((path) => {
        const destination = supportDestination(stored[path]);
        return destination?.split("?")[0] === path ? [[path, destination]] : [];
      }),
    );
  } catch {
    return {};
  }
}

export function SupportNavigationProvider({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const location = useLocation();
  const storageKey = `customy.support-workspaces:${userId}`;
  const [visits, setVisits] = useState(() => readVisits(storageKey));
  useEffect(() => {
    const current = supportDestination(location.pathname + location.search);
    if (!current) return;
    setVisits((previous) => {
      const next = { ...previous, [location.pathname]: current };
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* In-memory navigation remains available. */
      }
      return next;
    });
  }, [location.pathname, location.search, storageKey]);
  return <VisitsContext.Provider value={visits}>{children}</VisitsContext.Provider>;
}

export function useSupportNavigationTarget(target: string) {
  const visits = useContext(VisitsContext);
  return visits[target] || target;
}

export function SupportLink({
  to,
  ...props
}: Omit<ComponentProps<typeof Link>, "to"> & { to: string }) {
  const location = useLocation();
  const destination = supportDestination(to);
  const origin = supportDestination(location.pathname + location.search);
  const href =
    destination && origin && destination.split("?")[0] !== location.pathname
      ? `${destination}${destination.includes("?") ? "&" : "?"}from=${encodeURIComponent(origin)}`
      : to;
  return <Link {...props} to={href} />;
}

export function SupportBackLink({ className = "" }: { className?: string }) {
  const location = useLocation();
  const { t } = useTranslation();
  const destination = supportDestination(new URLSearchParams(location.search).get("from"));
  if (!destination || destination.split("?")[0] === location.pathname) return null;
  return (
    <Link
      to={destination}
      className={`mb-3 inline-flex min-h-9 items-center gap-1.5 rounded text-sm font-medium text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${className}`}
    >
      <span className="material-symbols-outlined text-lg" aria-hidden="true">
        arrow_back
      </span>
      {t(
        destination.startsWith("/messages")
          ? "supportNavigation.backToMessage"
          : "supportNavigation.backToReturn",
      )}
    </Link>
  );
}
